import { STORAGE_KEYS } from "../state/index.ts";
import type { PracticeLogEntry } from "../domain/progressRules.ts";
import type { PracticeSnapshot } from "../domain/practiceSnapshot.ts";

// The configuration lives in services/config.ts, which is the one place that reads and writes it.

// Collapse state
export function loadCollapseState() {
  try {
    return localStorage.getItem(STORAGE_KEYS.collapse);
  } catch (e) {
    return null;
  }
}

export function saveCollapseState(state: string) {
  try {
    localStorage.setItem(STORAGE_KEYS.collapse, state);
  } catch (e) {
    // ignore storage errors
  }
}

export function loadRepertoireCollapseState() {
  try {
    return localStorage.getItem(STORAGE_KEYS.repertoireCollapse);
  } catch (e) {
    return null;
  }
}

export function saveRepertoireCollapseState(state: string) {
  try {
    localStorage.setItem(STORAGE_KEYS.repertoireCollapse, state);
  } catch (e) {
    // ignore storage errors
  }
}

export function loadPlayerCollapseState() {
  try {
    return localStorage.getItem(STORAGE_KEYS.playerCollapse);
  } catch (e) {
    return null;
  }
}

export function savePlayerCollapseState(state: string) {
  try {
    localStorage.setItem(STORAGE_KEYS.playerCollapse, state);
  } catch (e) {
    // ignore storage errors
  }
}

// Onboarding collapse state
export function loadOnboardingCollapseState() {
  try {
    return localStorage.getItem(STORAGE_KEYS.onboardingCollapse);
  } catch (e) {
    return null;
  }
}

export function saveOnboardingCollapseState(state: string) {
  try {
    localStorage.setItem(STORAGE_KEYS.onboardingCollapse, state);
  } catch (e) {
    // ignore storage errors
  }
}

// AutoPlay
export function loadAutoPlay() {
  try {
    return localStorage.getItem(STORAGE_KEYS.autoPlay);
  } catch (e) {
    return null;
  }
}

export function saveAutoPlay(value: boolean) {
  try {
    localStorage.setItem(STORAGE_KEYS.autoPlay, value ? "true" : "false");
  } catch (e) {
    // ignore storage errors
  }
}


// Generic JSON helpers for the keys below
function loadJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) {
    return fallback;
  }
}

function saveJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // ignore storage errors
  }
}

// Practice log of a visitor (kept on this device until they create an account)
const PRACTICE_LOG_LIMIT = 50;

export function loadPracticeLog(): PracticeLogEntry[] {
  const log = loadJson<PracticeLogEntry[]>(STORAGE_KEYS.practiceLog, []);
  return Array.isArray(log) ? log : [];
}

export function savePracticeLog(log: PracticeLogEntry[]) {
  saveJson(STORAGE_KEYS.practiceLog, log.slice(-PRACTICE_LOG_LIMIT));
}

export function clearPracticeLog() {
  try {
    localStorage.removeItem(STORAGE_KEYS.practiceLog);
  } catch (e) {
    // ignore storage errors
  }
}

// Lines played most recently, so a random pick does not repeat them
const RECENT_LINES_LIMIT = 4;

export function loadRecentLines(): string[] {
  const recent = loadJson<string[]>(STORAGE_KEYS.recentLines, []);
  return Array.isArray(recent) ? recent : [];
}

export function pushRecentLine(slug: string) {
  const recent = loadRecentLines().filter((s) => s !== slug);
  recent.unshift(slug);
  saveJson(STORAGE_KEYS.recentLines, recent.slice(0, RECENT_LINES_LIMIT));
}

// Line to come back to after logging in: { slug, ts }
const RESUME_MAX_AGE_MS = 24 * 60 * 60 * 1000;   // verifying an email can take a while

export function saveResume(slug: string) {
  saveJson(STORAGE_KEYS.resume, { slug, ts: Date.now() });
}

export function takeResume(): string | null {
  const resume = loadJson<{ slug: string; ts: number } | null>(STORAGE_KEYS.resume, null);
  try {
    localStorage.removeItem(STORAGE_KEYS.resume);
  } catch (e) {
    // ignore storage errors
  }
  return resume && Date.now() - resume.ts < RESUME_MAX_AGE_MS ? resume.slug : null;
}

// Analysis panel
export function loadVerdictDetailsOpen() {
  // Open unless the user closed it: the breakdown of a verdict is the most useful part of the strip
  return loadJson<boolean>(STORAGE_KEYS.verdictDetailsOpen, true) !== false;
}

/** The study's engine: the evaluation hidden (off by default) and its lines shown (on by default). */
export function loadEngineEvalHidden() {
  return loadJson<boolean>(STORAGE_KEYS.engineEvalHidden, false) === true;
}

export function saveEngineEvalHidden(hidden: boolean) {
  saveJson(STORAGE_KEYS.engineEvalHidden, !!hidden);
}

export function loadEngineLines() {
  return loadJson<boolean>(STORAGE_KEYS.engineLines, true) !== false;
}

export function saveEngineLines(shown: boolean) {
  saveJson(STORAGE_KEYS.engineLines, !!shown);
}

/** The repertoire's moves from the position, listed under it: closed by default, they are the answer. */
export function loadRepertoireMovesOpen() {
  return loadJson<boolean>(STORAGE_KEYS.repertoireMovesOpen, false) === true;
}

export function saveRepertoireMovesOpen(open: boolean) {
  saveJson(STORAGE_KEYS.repertoireMovesOpen, !!open);
}

export function saveVerdictDetailsOpen(open: boolean) {
  saveJson(STORAGE_KEYS.verdictDetailsOpen, !!open);
}

export function loadAnalysisOpen() {
  return loadJson<boolean>(STORAGE_KEYS.analysisOpen, false) === true;
}

export function saveAnalysisOpen(open: boolean) {
  saveJson(STORAGE_KEYS.analysisOpen, !!open);
}

// How many of the last games the home page shows, per colour. A device thing, like the panels that
// are open: it is how much of that card somebody wants to look at, not a setting of their account.
export function loadLastGamesShown(fallback: number): number {
  const shown = loadJson<number>(STORAGE_KEYS.lastGamesShown, fallback);
  return typeof shown === "number" && shown > 0 ? shown : fallback;
}

export function saveLastGamesShown(shown: number): void {
  saveJson(STORAGE_KEYS.lastGamesShown, shown);
}

// How far back the ranking of the user's own games looks. A device thing, like the panels that are
// open: somebody with a month of games and somebody with five years want different windows, and
// neither is answering a question about their account.
export function loadLinesWindow(): number | null {
  const days = loadJson<number | null>(STORAGE_KEYS.linesWindow, null);
  return typeof days === "number" && days > 0 ? days : null;
}

export function saveLinesWindow(days: number): void {
  saveJson(STORAGE_KEYS.linesWindow, days);
}

// How /explore sorts what was played from a position. Most played unless somebody chose otherwise, and
// then what they chose: asking again on every position is a click per step down the tree.
export function loadExploreOrder(): "worst" | "played" {
  return loadJson<string>(STORAGE_KEYS.exploreOrder, "played") === "worst" ? "worst" : "played";
}

export function saveExploreOrder(order: "worst" | "played"): void {
  saveJson(STORAGE_KEYS.exploreOrder, order);
}

// The columns of the admin's user list, their order and which are shown: per device, like any layout
export function loadAdminUserColumns(): unknown {
  return loadJson<unknown>(STORAGE_KEYS.adminUserColumns, null);
}

export function saveAdminUserColumns(layout: unknown): void {
  saveJson(STORAGE_KEYS.adminUserColumns, layout);
}

// Whether the report splits an opening by its moves below the names. Off unless asked for: somebody
// starting out wants the names, and the branches are for somebody who has a repertoire.
export function loadReportMoves() {
  return loadJson<boolean>(STORAGE_KEYS.reportMoves, false) === true;
}

export function saveReportMoves(on: boolean) {
  saveJson(STORAGE_KEYS.reportMoves, !!on);
}

// Which window the analysis is reading: this week, or the last four. Per device, like the window of
// /lines — somebody who plays every day and somebody who plays on Sundays want different ones, and
// neither is answering a question about their account. Checked by domain/analysisWindow.readWindow.
export function loadAnalysisWindow(): unknown {
  return loadJson<unknown>(STORAGE_KEYS.analysisWindow, null);
}

export function saveAnalysisWindow(window: string) {
  saveJson(STORAGE_KEYS.analysisWindow, window);
}

// The fewest games an opening needs to be in the record of /analysis/openings; checked by readRecordMin
export function loadRecordMin(): unknown {
  return loadJson<unknown>(STORAGE_KEYS.analysisRecordMin, null);
}

export function saveRecordMin(games: number) {
  saveJson(STORAGE_KEYS.analysisRecordMin, games);
}

// The last analysis whose report this device has opened: the dashboard stops saying it is ready
export function loadReportSeenJob(): number | null {
  const id = loadJson<number | null>(STORAGE_KEYS.reportSeenJob, null);
  return typeof id === "number" ? id : null;
}

export function saveReportSeenJob(id: number) {
  saveJson(STORAGE_KEYS.reportSeenJob, id);
}

// The practice in progress, kept for the tab it is being played in.
//
// sessionStorage and not localStorage on purpose: two tabs can be on two different lines, and a
// practice left open yesterday is not one to come back to. It survives what it has to survive,
// which is navigating to another page of the site and back.

export function savePracticeSnapshot(snapshot: PracticeSnapshot) {
  try {
    sessionStorage.setItem(STORAGE_KEYS.practiceSnapshot, JSON.stringify(snapshot));
  } catch (e) {
    // ignore storage errors (private mode, quota)
  }
}

export function loadPracticeSnapshot(): PracticeSnapshot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS.practiceSnapshot);
    return raw ? (JSON.parse(raw) as PracticeSnapshot) : null;
  } catch (e) {
    return null;
  }
}

export function clearPracticeSnapshot() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.practiceSnapshot);
  } catch (e) {
    // ignore storage errors
  }
}

// ---------------------------------------------------------------------------------------------
// What the engine said about a position the user came out of an opening in
// ---------------------------------------------------------------------------------------------

/**
 * Evaluations of opening positions, kept on this device.
 *
 * The home page judges how the last games came out of their opening, and that is Stockfish work in
 * this browser: a few seconds the first time, and nothing at all afterwards. Keyed by FEN, so two
 * games that reached the same position are one question. It is a cache of something expensive and
 * never of something true: a browser that will not remember it just does the work again.
 */
const OPENING_EDGE_KEY = "sparring_opening_edges";
const OPENING_EDGE_LIMIT = 300;

export interface OpeningScore {
  cp: number | null;
  mate: number | null;
}

/**
 * The whole cache at once, and not one position at a time: whoever reads this is judging a list of
 * games, and asking per game parses the same blob five hundred times. They also have to count what
 * they do not know yet before starting, which is the same read again.
 */
export function loadOpeningEdges(): Record<string, OpeningScore> {
  const all = loadJson<Record<string, OpeningScore>>(OPENING_EDGE_KEY, {});
  return all && typeof all === "object" ? all : {};
}

export function saveOpeningEdge(fen: string, score: OpeningScore): void {
  const all = loadJson<Record<string, OpeningScore>>(OPENING_EDGE_KEY, {}) || {};
  const keys = Object.keys(all);
  if (keys.length >= OPENING_EDGE_LIMIT) {
    // Oldest first: insertion order is what the object keeps, and this is a cache, not a record
    for (const old of keys.slice(0, keys.length - OPENING_EDGE_LIMIT + 1)) delete all[old];
  }
  all[fen] = score;
  saveJson(OPENING_EDGE_KEY, all);
}
